import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Users, Building, Grid3X3, ClipboardList, Inbox, FileText,
  GraduationCap, Briefcase, FolderCheck, Activity, ChevronRight, HeartPulse,
} from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

// Founder small improvement batch (2026-09-05) — one-page tenant health
// snapshot. Every figure comes from an existing canonical read model.
const Tile = ({ icon: Icon, label, value, hint, to, accent = 'primary', navigate, testid, badges }) => (
  <Card
    className="p-4 shadow-card cursor-pointer transition-colors hover:border-primary/40 group"
    onClick={() => to && navigate(to)}
    data-testid={testid}
  >
    <div className="flex items-start gap-3">
      <div className={`h-9 w-9 rounded-lg bg-${accent}/10 text-${accent} flex items-center justify-center shrink-0`}>
        <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
        <p className="font-display text-2xl font-bold tabular-nums leading-tight mt-0.5">{value}</p>
        {hint && <p className="text-xs text-muted-foreground mt-0.5 truncate">{hint}</p>}
        {badges && <div className="flex flex-wrap gap-1.5 mt-1.5">{badges}</div>}
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-primary transition-colors mt-1" />
    </div>
  </Card>
);

const Section = ({ title, children }) => (
  <div>
    <h2 className="text-sm uppercase tracking-wider font-semibold text-muted-foreground mb-2.5">{title}</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">{children}</div>
  </div>
);

export default function ClientHealthSnapshot() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clientApi.healthSnapshot()
      .then(setData)
      .catch((e) => toast.error(t('toasts.loadFailed'), { description: formatApiError(e) }))
      .finally(() => setLoading(false));
  }, [t]);

  const fmtDate = (iso) => (iso
    ? new Date(iso).toLocaleDateString(i18n.language?.startsWith('es') ? 'es' : 'en', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—');

  if (loading || !data) {
    return (
      <>
        <Topbar title={t('clientHealth.title')} subtitle={t('clientHealth.subtitle')} />
        <div className="p-6 lg:p-8"><Card className="p-8 text-center text-muted-foreground" data-testid="health-loading">…</Card></div>
      </>
    );
  }

  const { directory, suites_active: suites, operations: ops, talenttrain: tt, onboarding: onb, workforce: wr, climara } = data;

  return (
    <>
      <Topbar title={t('clientHealth.title')} subtitle={t('clientHealth.subtitle')} />
      <div className="p-6 lg:p-8 space-y-6" data-testid="client-health-snapshot">
        <Section title={t('clientHealth.org')}>
          <Tile icon={Users} navigate={navigate} to="/client/employees" testid="health-collaborators"
                label={t('clientHealth.collaborators')} value={directory.evaluable}
                hint={t('clientHealth.collaboratorsHint', { total: directory.total })} />
          <Tile icon={Building} navigate={navigate} to="/client/departments" testid="health-departments"
                label={t('clientHealth.departments')} value={directory.departments} />
          <Tile icon={Grid3X3} navigate={navigate} to="/client/suites" testid="health-suites" accent="info"
                label={t('clientHealth.suites')} value={suites.length}
                hint={t('clientHealth.suitesHint')} />
        </Section>

        <Section title={t('clientHealth.opsSection')}>
          <Tile icon={ClipboardList} navigate={navigate} to="/client/requests" testid="health-evals"
                label={t('clientHealth.evalsInProgress')} value={ops.evaluations_in_progress} accent="warning" />
          <Tile icon={Inbox} navigate={navigate} to="/client/requests" testid="health-pending"
                label={t('clientHealth.pendingRequests')} value={ops.pending_requests} />
          <Tile icon={FileText} navigate={navigate} to="/client/reports" testid="health-reports" accent="success"
                label={t('clientHealth.reportsReleased')} value={ops.reports_released} />
        </Section>

        <Section title={t('clientHealth.suitesSection')}>
          <Tile icon={GraduationCap} navigate={navigate} to="/client/talenttrain" testid="health-talenttrain"
                label={t('clientHealth.recertAlerts')} value={tt.alerts} accent={tt.expired ? 'destructive' : 'warning'}
                badges={tt.alerts > 0 ? [
                  tt.expired > 0 && (
                    <Badge key="e" className="bg-destructive/15 text-destructive border-destructive/30">
                      {t('clientHealth.expired', { n: tt.expired })}
                    </Badge>),
                  tt.expiring > 0 && (
                    <Badge key="s" className="bg-warning/15 text-warning border-warning/30">
                      {t('clientHealth.expiring', { n: tt.expiring })}
                    </Badge>),
                ].filter(Boolean) : null}
                hint={tt.alerts === 0 ? t('clientHealth.noAlerts') : null} />
          <Tile icon={Briefcase} navigate={navigate} to="/client/talentstart" testid="health-onboarding"
                label={t('clientHealth.onboarding')} value={onb.active}
                hint={t('clientHealth.onboardingHint', { completed: onb.completed, overdue: onb.overdue })} />
          <Tile icon={FolderCheck} navigate={navigate} to="/client/workforce-records" testid="health-workforce"
                label={t('clientHealth.workforce')}
                value={wr.avg_completion != null ? `${wr.avg_completion}%` : '—'}
                accent={wr.expired ? 'destructive' : 'success'}
                hint={t('clientHealth.workforceHint', { expired: wr.expired, soon: wr.expiring_soon })} />
          <Tile icon={Activity} navigate={navigate} to="/client/climara" testid="health-climara" accent="info"
                label={t('clientHealth.climara')}
                value={climara ? `${climara.responded}/${climara.invited}` : '—'}
                hint={climara
                  ? t('clientHealth.climaraHint', { pct: climara.rate_pct, date: fmtDate(climara.released_at) })
                  : t('clientHealth.climaraNone')} />
        </Section>
      </div>
    </>
  );
}
